import { type ReactElement, useEffect, useRef, useState } from 'react';
import { Box, CloseButton, HStack, Spinner, Text, useTheme } from '@chakra-ui/react';

import { createPortal } from 'react-dom';

export type AppToastStatus = 'loading' | 'success' | 'error' | 'warning' | 'info';

export type AppToastProps = {
	status: AppToastStatus;
	title: string;
	description?: string;
	onClose?: () => void;
};

const ICON_SIZE = 20;
const CIRCLE_R = 8;
const CIRCLE_CIRCUMFERENCE = 2 * Math.PI * CIRCLE_R;

const bgColors: Record<AppToastStatus, string> = {
	loading: 'blue.500',
	success: 'green.500',
	error: 'red.500',
	warning: 'orange.400',
	info: 'blue.500',
};

// SVG animated icons — circle draws first, then the inner mark strokes in
const AnimatedCheckIcon = (): ReactElement => (
	<svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 20 20" fill="none">
		<circle
			cx="10" cy="10" r={CIRCLE_R}
			stroke="white" strokeWidth="1.5" fill="none"
			strokeDasharray={CIRCLE_CIRCUMFERENCE}
			strokeDashoffset={CIRCLE_CIRCUMFERENCE}
			strokeLinecap="round"
			style={{ animation: 'toast-circle-draw 0.4s ease-out forwards' }}
		/>
		<polyline
			points="6.5,10.5 9,13 13.5,7.5"
			stroke="white" strokeWidth="1.5" fill="none"
			strokeLinecap="round" strokeLinejoin="round"
			strokeDasharray="12"
			strokeDashoffset="12"
			style={{ animation: 'toast-mark-draw 0.3s ease-out 0.35s forwards' }}
		/>
	</svg>
);

const AnimatedXIcon = (): ReactElement => (
	<svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 20 20" fill="none">
		<circle
			cx="10" cy="10" r={CIRCLE_R}
			stroke="white" strokeWidth="1.5" fill="none"
			strokeDasharray={CIRCLE_CIRCUMFERENCE}
			strokeDashoffset={CIRCLE_CIRCUMFERENCE}
			strokeLinecap="round"
			style={{ animation: 'toast-circle-draw 0.4s ease-out forwards' }}
		/>
		<line
			x1="7" y1="7" x2="13" y2="13"
			stroke="white" strokeWidth="1.5" strokeLinecap="round"
			strokeDasharray="8.5"
			strokeDashoffset="8.5"
			style={{ animation: 'toast-mark-draw 0.25s ease-out 0.3s forwards' }}
		/>
		<line
			x1="13" y1="7" x2="7" y2="13"
			stroke="white" strokeWidth="1.5" strokeLinecap="round"
			strokeDasharray="8.5"
			strokeDashoffset="8.5"
			style={{ animation: 'toast-mark-draw 0.25s ease-out 0.4s forwards' }}
		/>
	</svg>
);

const AnimatedWarningIcon = (): ReactElement => (
	<svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 20 20" fill="none">
		<path
			d="M10 2.5 L18.5 16.5 Q19 17.5 18 17.5 L2 17.5 Q1 17.5 1.5 16.5 Z"
			stroke="white" strokeWidth="1.5" fill="none"
			strokeLinejoin="round"
			strokeDasharray="48"
			strokeDashoffset="48"
			style={{ animation: 'toast-circle-draw 0.4s ease-out forwards' }}
		/>
		<line
			x1="10" y1="8" x2="10" y2="12"
			stroke="white" strokeWidth="1.5" strokeLinecap="round"
			strokeDasharray="4"
			strokeDashoffset="4"
			style={{ animation: 'toast-mark-draw 0.2s ease-out 0.35s forwards' }}
		/>
		<circle
			cx="10" cy="14.5" r="0.75"
			fill="white" opacity="0"
			style={{ animation: 'toast-dot-in 0.15s ease-out 0.5s forwards' }}
		/>
	</svg>
);

const AnimatedInfoIcon = (): ReactElement => (
	<svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 20 20" fill="none">
		<circle
			cx="10" cy="10" r={CIRCLE_R}
			stroke="white" strokeWidth="1.5" fill="none"
			strokeDasharray={CIRCLE_CIRCUMFERENCE}
			strokeDashoffset={CIRCLE_CIRCUMFERENCE}
			strokeLinecap="round"
			style={{ animation: 'toast-circle-draw 0.4s ease-out forwards' }}
		/>
		<circle
			cx="10" cy="7" r="0.75"
			fill="white" opacity="0"
			style={{ animation: 'toast-dot-in 0.15s ease-out 0.3s forwards' }}
		/>
		<line
			x1="10" y1="9.5" x2="10" y2="14"
			stroke="white" strokeWidth="1.5" strokeLinecap="round"
			strokeDasharray="4.5"
			strokeDashoffset="4.5"
			style={{ animation: 'toast-mark-draw 0.2s ease-out 0.4s forwards' }}
		/>
	</svg>
);

const animatedIcons: Record<Exclude<AppToastStatus, 'loading'>, () => ReactElement> = {
	success: AnimatedCheckIcon,
	error: AnimatedXIcon,
	warning: AnimatedWarningIcon,
	info: AnimatedInfoIcon,
};

// Static icons for one-shot toasts (no loading → result transition)
const StaticCheckIcon = (): ReactElement => (
	<svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 20 20" fill="none">
		<circle cx="10" cy="10" r={CIRCLE_R} stroke="white" strokeWidth="1.5" fill="none" strokeLinecap="round" />
		<polyline points="6.5,10.5 9,13 13.5,7.5" stroke="white" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
	</svg>
);

const StaticXIcon = (): ReactElement => (
	<svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 20 20" fill="none">
		<circle cx="10" cy="10" r={CIRCLE_R} stroke="white" strokeWidth="1.5" fill="none" strokeLinecap="round" />
		<line x1="7" y1="7" x2="13" y2="13" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
		<line x1="13" y1="7" x2="7" y2="13" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
	</svg>
);

const StaticWarningIcon = (): ReactElement => (
	<svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 20 20" fill="none">
		<path d="M10 2.5 L18.5 16.5 Q19 17.5 18 17.5 L2 17.5 Q1 17.5 1.5 16.5 Z" stroke="white" strokeWidth="1.5" fill="none" strokeLinejoin="round" />
		<line x1="10" y1="8" x2="10" y2="12" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
		<circle cx="10" cy="14.5" r="0.75" fill="white" />
	</svg>
);

const StaticInfoIcon = (): ReactElement => (
	<svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 20 20" fill="none">
		<circle cx="10" cy="10" r={CIRCLE_R} stroke="white" strokeWidth="1.5" fill="none" strokeLinecap="round" />
		<circle cx="10" cy="7" r="0.75" fill="white" />
		<line x1="10" y1="9.5" x2="10" y2="14" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
	</svg>
);

const staticIcons: Record<Exclude<AppToastStatus, 'loading'>, () => ReactElement> = {
	success: StaticCheckIcon,
	error: StaticXIcon,
	warning: StaticWarningIcon,
	info: StaticInfoIcon,
};

// Global keyframes injected once
const KEYFRAMES_ID = 'app-toast-keyframes';
const ensureKeyframes = (): void => {
	if (document.getElementById(KEYFRAMES_ID)) return;
	const style = document.createElement('style');
	style.id = KEYFRAMES_ID;
	style.textContent = `
		@keyframes toast-circle-draw {
			to { stroke-dashoffset: 0; }
		}
		@keyframes toast-mark-draw {
			to { stroke-dashoffset: 0; }
		}
		@keyframes toast-dot-in {
			to { opacity: 1; }
		}
		@keyframes toast-spinner-fade {
			0% { opacity: 1; transform: scale(1); }
			100% { opacity: 0; transform: scale(0.3); }
		}
	`;
	document.head.appendChild(style);
};

export const AppToast = (props: AppToastProps): ReactElement => {
	const { status, title, description, onClose } = props;
	const theme = useTheme();
	const prevStatusRef = useRef<AppToastStatus>(status);
	const [transitioning, setTransitioning] = useState(false);
	const [showAnimatedIcon, setShowAnimatedIcon] = useState(false);

	useEffect(() => {
		ensureKeyframes();
	}, []);

	useEffect(() => {
		const wasLoading = prevStatusRef.current === 'loading';
		prevStatusRef.current = status;

		if (wasLoading && status !== 'loading') {
			// Spinner fades out, then animated icon draws in
			setTransitioning(true);
			const timer = setTimeout(() => {
				setShowAnimatedIcon(true);
				setTransitioning(false);
			}, 200);
			return () => clearTimeout(timer);
		}
	}, [status]);

	const bg = bgColors[status];
	const backdropZIndex = theme.z?.toast || 1700;
	const toastZIndex = backdropZIndex + 1;
	const isLoading = status === 'loading';

	// Determine which icon to render
	const renderIcon = (): ReactElement | null => {
		if (isLoading || transitioning) {
			return (
				<Box
					display="flex" alignItems="center" justifyContent="center"
					sx={transitioning ? {
						animation: 'toast-spinner-fade 0.2s ease-in forwards',
					} : undefined}
				>
					<Spinner size="sm" color="white" thickness="2.5px" speed="0.8s" />
				</Box>
			);
		}

		if (status === 'loading') return null;

		const IconComponent = showAnimatedIcon
			? animatedIcons[status]
			: staticIcons[status];

		return <IconComponent />;
	};

	return (
		<>
			{isLoading && createPortal(
				<Box
					position="fixed"
					top={0}
					left={0}
					right={0}
					bottom={0}
					w="100vw"
					h="100vh"
					zIndex={backdropZIndex}
					bg="blackAlpha.500"
					pointerEvents="all"
					cursor="not-allowed"
				/>,
				document.body
			)}
			<HStack
					bg={bg}
					color="white"
					px={6}
					py={4}
					borderRadius="md"
					boxShadow="lg"
					spacing={4}
					align="center"
					minW="280px"
					sx={{ transition: 'background-color 0.3s ease' }}
				>
					<Box w={`${ICON_SIZE}px`} h={`${ICON_SIZE}px`} flexShrink={0} display="flex" alignItems="center" justifyContent="center">
						{renderIcon()}
					</Box>
					<Box flex={1}>
						<Text fontSize="md" fontWeight="medium">
							{title}
						</Text>
						{description && (
							<Text fontSize="sm" fontWeight="normal" opacity={0.9} mt={1} whiteSpace="pre-line">
								{description}
							</Text>
						)}
					</Box>
					{!isLoading && !transitioning && onClose && (
						<CloseButton
							size="sm"
							color="white"
							opacity={0.8}
							_hover={{ opacity: 1 }}
							onClick={onClose}
						/>
					)}
			</HStack>
		</>
	);
};
